export type JsonObject = Record<string, unknown>;

export function object(value: unknown): JsonObject {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new TypeError("Expected an object.");
  }
  return value as JsonObject;
}

export function text(value: unknown, allowEmpty = false): string {
  if (typeof value !== "string" || (!allowEmpty && value.trim().length === 0)) {
    throw new TypeError("Expected text.");
  }
  return value;
}

export function identifier(value: unknown): string {
  const result = text(value);
  if (!/^[A-Za-z0-9_-]{1,64}$/.test(result)) throw new TypeError("Invalid identifier.");
  return result;
}

export function secret(value: unknown): string {
  const result = text(value);
  if (!isQrSecret(result)) throw new TypeError("Invalid QR code.");
  return result;
}

export function isQrSecret(value: string): boolean {
  return /^[A-Za-z0-9_-]{43}$/.test(value);
}

export function integer(value: unknown, minimum = 0): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < minimum) {
    throw new TypeError("Invalid count.");
  }
  return value;
}

function boolean(value: unknown): boolean {
  if (typeof value !== "boolean") throw new TypeError("Expected a boolean.");
  return value;
}

export function timestamp(value: unknown): string {
  const result = text(value);
  if (!result.endsWith("Z") || !Number.isFinite(Date.parse(result))) {
    throw new TypeError("Invalid timestamp.");
  }
  return result;
}

export function list<T>(value: unknown, parse: (item: unknown) => T): T[] {
  if (!Array.isArray(value)) throw new TypeError("Expected a list.");
  return value.map(parse);
}

export function unique<T>(items: T[], key: (item: T) => string | number): T[] {
  if (new Set(items.map(key)).size !== items.length) {
    throw new TypeError("Duplicate identities.");
  }
  return items;
}

export interface Branch {
  readonly id: string;
  readonly name: string;
}

export function parseBranch(value: unknown): Branch {
  const data = object(value);
  return { id: identifier(data.id), name: text(data.name) };
}

export interface Project {
  readonly id: string;
  readonly name: string;
  readonly branch: Branch;
  readonly electoral_register_id: string | null;
}

export interface ProjectSummary extends Project {
  readonly created_at: string;
  readonly updated_at: string;
  readonly canvassed_count: number;
}

export function parseProject(value: unknown): Project {
  const data = object(value);
  const registerId = data.electoral_register_id;
  if (
    registerId !== null &&
    (typeof registerId !== "string" || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(registerId))
  ) {
    throw new TypeError("Invalid electoral roll.");
  }
  return {
    id: identifier(data.id),
    name: text(data.name),
    branch: parseBranch(data.branch),
    electoral_register_id: registerId,
  };
}

export function parseProjectSummary(value: unknown): ProjectSummary {
  const data = object(value);
  const project = parseProject(data);
  const created_at = timestamp(data.created_at);
  const updated_at = timestamp(data.updated_at);
  if (Date.parse(updated_at) < Date.parse(created_at))
    throw new TypeError("Invalid update time.");
  return { ...project, created_at, updated_at, canvassed_count: integer(data.canvassed_count) };
}

export interface User {
  readonly id: string;
  readonly display_name: string;
  readonly email: string;
}

export function parseUser(value: unknown): User {
  const data = object(value);
  return {
    id: identifier(data.id),
    display_name: text(data.display_name),
    email: text(data.email),
  };
}

export interface Participant {
  readonly user: User;
  readonly joined_at: string;
}

export interface Session {
  readonly id: string;
  readonly project: Project;
  readonly created_by_user_id: string;
  readonly started_at: string;
  readonly expires_at: string;
  readonly maximum_ends_at: string;
  readonly participants: readonly Participant[];
}

export interface IssuedSession {
  readonly session: Session;
  readonly join_code: string;
}

export function parseSession(value: unknown): Session {
  const data = object(value);
  const started_at = timestamp(data.started_at);
  const expires_at = timestamp(data.expires_at);
  const maximum_ends_at = timestamp(data.maximum_ends_at);
  if (
    Date.parse(started_at) >= Date.parse(expires_at) ||
    Date.parse(maximum_ends_at) < Date.parse(expires_at)
  ) {
    throw new TypeError("Invalid session duration.");
  }
  return {
    id: identifier(data.id),
    project: parseProject(data.project),
    created_by_user_id: identifier(data.created_by_user_id),
    started_at,
    expires_at,
    maximum_ends_at,
    participants: unique(
      list(data.participants, (value) => {
        const person = object(value);
        return { user: parseUser(person.user), joined_at: timestamp(person.joined_at) };
      }),
      (person) => person.user.id,
    ),
  };
}

export function parseIssuedSession(value: unknown): IssuedSession {
  const data = object(value);
  return { session: parseSession(data.session), join_code: secret(data.join_code) };
}

export interface Register {
  readonly id: string;
  readonly label: string;
  readonly snapshot_id: string;
}

function digest(value: unknown): string {
  const result = text(value);
  if (!/^[a-f0-9]{64}$/.test(result)) throw new TypeError("Invalid electoral identity.");
  return result;
}

export function parseRegister(value: unknown): Register {
  const data = object(value);
  const id = identifier(data.id);
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(id)) throw new TypeError("Invalid electoral roll.");
  return { id, label: text(data.label), snapshot_id: digest(data.snapshot_id) };
}

export interface Position {
  readonly latitude: number;
  readonly longitude: number;
}
export type Coordinate = [number, number];

export function parsePosition(value: unknown): Position {
  const data = object(value);
  const latitude = data.latitude;
  const longitude = data.longitude;
  if (
    typeof latitude !== "number" ||
    !Number.isFinite(latitude) ||
    Math.abs(latitude) > 90 ||
    typeof longitude !== "number" ||
    !Number.isFinite(longitude) ||
    Math.abs(longitude) > 180
  ) {
    throw new TypeError("Invalid location.");
  }
  return { latitude, longitude };
}

export interface PeerLocation extends Position {
  readonly user: User;
  readonly updated_at: string;
}

export function parsePeerLocation(value: unknown): PeerLocation {
  const data = object(value);
  return {
    ...parsePosition(data),
    user: parseUser(data.user),
    updated_at: timestamp(data.updated_at),
  };
}

export interface StreetRecord {
  readonly usrn: number;
  readonly completed: boolean;
  readonly revision: number;
  readonly user_id: string | null;
  readonly changed_at: string | null;
}

export function parseStreetRecord(value: unknown): StreetRecord {
  const data = object(value);
  const revision = integer(data.revision);
  const completed = boolean(data.completed);
  const user_id = data.user_id === null ? null : identifier(data.user_id);
  const changed_at = data.changed_at === null ? null : timestamp(data.changed_at);
  if (
    (revision === 0 && (completed || user_id !== null || changed_at !== null)) ||
    (revision > 0 && (user_id === null || changed_at === null))
  ) {
    throw new TypeError("Invalid street completion history.");
  }
  return { usrn: integer(data.usrn, 1), completed, revision, user_id, changed_at };
}

export interface Road {
  readonly usrn: number;
  readonly name: string;
  readonly distance_meters: number;
  readonly lines: readonly Coordinate[][];
  readonly canvass: StreetRecord | null;
}

export function parseRoad(value: unknown): Road {
  const data = object(value);
  const usrn = integer(data.usrn, 1);
  const distance = data.distance_meters;
  if (typeof distance !== "number" || !Number.isFinite(distance) || distance < 0) {
    throw new TypeError("Invalid distance.");
  }
  const lines = list(data.lines, (value) => {
    const line = list(value, (value): Coordinate => {
      if (!Array.isArray(value) || value.length !== 2)
        throw new TypeError("Invalid road geometry.");
      const point = parsePosition({ longitude: value[0], latitude: value[1] });
      return [point.longitude, point.latitude];
    });
    if (line.length < 2) throw new TypeError("Invalid road line.");
    return line;
  });
  if (lines.length === 0) throw new TypeError("Missing road geometry.");
  const canvass = data.canvass == null ? null : parseStreetRecord(data.canvass);
  if (canvass !== null && canvass.usrn !== usrn)
    throw new TypeError("Street identity mismatch.");
  return { usrn, name: text(data.name), distance_meters: distance, lines, canvass };
}

export const outcomes = [
  ["yes", "Yes"],
  ["considering", "Considering"],
  ["undecided", "Undecided"],
  ["other_party", "Other Party"],
  ["not_interested", "Not Interested"],
  ["no", "No"],
  ["non_voter", "Non-Voter"],
] as const;
export type Outcome = (typeof outcomes)[number][0];
export const parties = [
  ["labour", "Labour"],
  ["reform", "Reform"],
  ["conservatives", "Conservatives"],
  ["green", "Green"],
  ["restore", "Restore"],
] as const;
export type Party = (typeof parties)[number][0];
export const statuses = [
  ["out", "Out"],
  ["moved", "Moved"],
  ["blacklist", "Blacklist"],
] as const;
export type VoterStatus = (typeof statuses)[number][0];
export interface Answers {
  readonly general_outcome: Outcome | null;
  readonly general_party: Party | null;
  readonly local_outcome: Outcome | null;
  readonly local_party: Party | null;
  readonly voter_status: VoterStatus | null;
}

function option<T extends string>(
  value: unknown,
  options: readonly (readonly [T, string])[],
): T | null {
  if (value === null) return null;
  if (!options.some(([id]) => id === value)) throw new TypeError("Invalid canvassing answer.");
  return value as T;
}

export function parseAnswers(value: unknown): Answers {
  const data = object(value);
  const result = {
    general_outcome: option(data.general_outcome, outcomes),
    general_party: option(data.general_party, parties),
    local_outcome: option(data.local_outcome, outcomes),
    local_party: option(data.local_party, parties),
    voter_status: option(data.voter_status, statuses),
  };
  if (
    (result.general_outcome === "other_party") !== (result.general_party !== null) ||
    (result.local_outcome === "other_party") !== (result.local_party !== null)
  ) {
    throw new TypeError("An Other Party answer requires its own party.");
  }
  return result;
}

export interface ElectorRecord extends Answers {
  readonly elector_id: string;
  readonly notes: string;
  readonly user_id: string | null;
  readonly changed_at: string | null;
}

export function parseElectorRecord(value: unknown): ElectorRecord {
  const data = object(value);
  const user_id = data.user_id === null ? null : identifier(data.user_id);
  const changed_at = data.changed_at === null ? null : timestamp(data.changed_at);
  if ((user_id === null) !== (changed_at === null))
    throw new TypeError("Invalid result history.");
  return {
    ...parseAnswers(data),
    elector_id: digest(data.elector_id),
    notes: text(data.notes, true),
    user_id,
    changed_at,
  };
}

export interface Voter {
  readonly elector_id: string;
  readonly name: string;
  readonly canvass: Answers | null;
}
export interface House {
  readonly address_id: string;
  readonly address_lines: readonly string[];
  readonly postcode: string;
  readonly registered_voters: readonly Voter[];
}

export function parseHouse(value: unknown): House {
  const data = object(value);
  const address_lines = list(data.address_lines, (value) => text(value));
  const registered_voters = unique(
    list(data.registered_voters, (value): Voter => {
      const voter = object(value);
      if (!("canvass" in voter)) throw new TypeError("Missing voter summary.");
      return {
        elector_id: digest(voter.elector_id),
        name: text(voter.name),
        canvass: voter.canvass === null ? null : parseAnswers(voter.canvass),
      };
    }),
    (item) => item.elector_id,
  );
  if (address_lines.length === 0 || registered_voters.length === 0)
    throw new TypeError("Empty household.");
  return {
    address_id: text(data.address_id),
    address_lines,
    postcode: text(data.postcode),
    registered_voters,
  };
}

export interface MembershipPass {
  readonly code: string;
  readonly expires_at: string;
}
export function parseMembershipPass(value: unknown): MembershipPass {
  const data = object(value);
  const code = text(data.code);
  if (!/^groundspark:membership:[A-Za-z0-9_-]{43}$/.test(code))
    throw new TypeError("Invalid membership code.");
  return { code, expires_at: timestamp(data.expires_at) };
}
export type MembershipVerdict =
  | { readonly valid: false }
  | {
      readonly valid: true;
      readonly first_name: string;
      readonly last_name: string;
      readonly branch: Branch;
    };
export function parseMembershipVerdict(value: unknown): MembershipVerdict {
  const data = object(value);
  if (boolean(data.valid))
    return {
      valid: true,
      first_name: text(data.first_name),
      last_name: text(data.last_name),
      branch: parseBranch(data.branch),
    };
  if (data.first_name != null || data.last_name != null || data.branch != null)
    throw new TypeError("Invalid membership verdict.");
  return { valid: false };
}

export interface Invitation {
  readonly kind: "email" | "organiser_qr";
  readonly organization: Branch;
  readonly branch: Branch;
  readonly expires_at: string;
}
export interface IssuedInvitation extends Invitation {
  readonly id: string;
  readonly code: string;
}
export function parseInvitation(value: unknown): Invitation {
  const data = object(value);
  if (data.kind !== "email" && data.kind !== "organiser_qr")
    throw new TypeError("Invalid invitation kind.");
  return {
    kind: data.kind,
    organization: parseBranch(data.organization),
    branch: parseBranch(data.branch),
    expires_at: timestamp(data.expires_at),
  };
}
export function parseIssuedInvitation(value: unknown): IssuedInvitation {
  const data = object(value);
  return { ...parseInvitation(data), id: identifier(data.id), code: secret(data.code) };
}

export interface Notification {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly branch_name: string;
  readonly created_at: string;
  readonly read_at: string | null;
}
export function parseNotification(value: unknown): Notification {
  const data = object(value);
  const created_at = timestamp(data.created_at);
  const read_at = data.read_at === null ? null : timestamp(data.read_at);
  if (read_at !== null && Date.parse(read_at) < Date.parse(created_at))
    throw new TypeError("Invalid read time.");
  return {
    id: identifier(data.id),
    title: text(data.title),
    body: text(data.body),
    branch_name: text(data.branch_name),
    created_at,
    read_at,
  };
}

export type Selection = { readonly kind: "session" | "project"; readonly id: string };

export function remaining(expiresAt: string, now = Date.now()): string {
  const seconds = Math.max(0, Math.ceil((Date.parse(expiresAt) - now) / 1000));
  if (seconds === 0) return "Expired";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours > 0 ? `${hours}h ` : ""}${minutes}m ${seconds % 60}s`;
}

export function dateLabel(value: string | null): string {
  return value === null
    ? "—"
    : new Date(value).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}
